import React, {useState, useRef, useEffect} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {H2, P} from '@/typography';
import {withMarginBottomMd, withMarginBottomXxl} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import useComplianceTabItems from '@/ui/tabs/tab-items/compliance-tabs';
import {handleDropdown, scrollToTopOfPage, scrollableDropdownMob} from '@/ui/legal/legal.helper';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import webAccessibilityStatement from '../messages/en/webAccessibilityStatement.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('webAccessibilityStatement.seo.title'),
    description: seoT('webAccessibilityStatement.seo.description'),
    canonical: seoT('webAccessibilityStatement.seo.canonical')
  };
};

const MarginedH2 = withMarginBottomMd(H2);
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const AccessibilityStatementPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('webAccessibilityStatement');
  const legalT = useTranslations('legal');
  const tabItems = useComplianceTabItems();

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="web-accessibility"
        title={legalT('sectionTitles.compliance')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'])}>
        <Dropdown
          name="compliance"
          defaultValue="web-accessibility"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const AccessibilityStatementContent = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      {t.raw('paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}
      <P>
        {t.rich('contactParagraph', {
          a: chunks => (
            <Link href="mailto:support@workable.com" variant="default">
              {chunks}
            </Link>
          )
        })}
      </P>
    </>
  );
  return (
    <>
      <LegalHero title={legalT('sectionTitles.compliance')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.compliance')}
                activeTab={tabItems.find(item => item.value === 'web-accessibility').text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <AccessibilityStatementContent />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(AccessibilityStatementPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, webAccessibilityStatement};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
